import React, { SyntheticEvent, useCallback, useState } from 'react';
import './BookCoverFrame.css';

type BookCoverFrameProps = {
  src: string;
  alt: string;
  srcSet?: string;
  sizes?: string;
  className?: string;
  imageClassName?: string;
};

const COLOR_CACHE = new Map<string, string>();
const SAMPLE_SIZE = 32;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const mix = (value: number, target: number, amount: number) => value + (target - value) * amount;

const normalizeColor = (red: number, green: number, blue: number) => {
  const brightness = 0.2126 * red + 0.7152 * green + 0.0722 * blue;
  let r = red;
  let g = green;
  let b = blue;

  if (brightness > 205) {
    r = mix(r, 118, 0.38);
    g = mix(g, 88, 0.38);
    b = mix(b, 96, 0.38);
  } else if (brightness < 54) {
    r = mix(r, 116, 0.36);
    g = mix(g, 86, 0.36);
    b = mix(b, 94, 0.36);
  }

  return `rgb(${Math.round(clamp(r, 42, 214))}, ${Math.round(clamp(g, 42, 214))}, ${Math.round(clamp(b, 42, 214))})`;
};

const getAverageCoverColor = (image: HTMLImageElement): string | undefined => {
  const sourceKey = image.currentSrc || image.src;
  const cached = COLOR_CACHE.get(sourceKey);
  if (cached) return cached;

  if (!image.naturalWidth || !image.naturalHeight) return undefined;

  const canvas = document.createElement('canvas');
  canvas.width = SAMPLE_SIZE;
  canvas.height = SAMPLE_SIZE;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return undefined;

  try {
    context.drawImage(image, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
    const pixels = context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data;

    let red = 0;
    let green = 0;
    let blue = 0;
    let totalWeight = 0;

    for (let index = 0; index < pixels.length; index += 4) {
      const alpha = pixels[index + 3] / 255;
      if (alpha < 0.18) continue;

      const r = pixels[index];
      const g = pixels[index + 1];
      const b = pixels[index + 2];
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const saturation = max === 0 ? 0 : (max - min) / max;
      const brightness = (r + g + b) / 3;
      let weight = alpha * (1 + saturation * 1.4);

      if (brightness > 238 && saturation < 0.14) weight *= 0.08;
      if (brightness < 18) weight *= 0.25;

      red += r * weight;
      green += g * weight;
      blue += b * weight;
      totalWeight += weight;
    }

    if (totalWeight <= 0) return undefined;

    const color = normalizeColor(red / totalWeight, green / totalWeight, blue / totalWeight);
    COLOR_CACHE.set(sourceKey, color);
    return color;
  } catch {
    return undefined;
  }
};

function BookCoverFrame({
  src,
  alt,
  srcSet,
  sizes,
  className,
  imageClassName,
}: BookCoverFrameProps) {
  const [backgroundColor, setBackgroundColor] = useState<string>();

  const handleLoad = useCallback((event: SyntheticEvent<HTMLImageElement>) => {
    const color = getAverageCoverColor(event.currentTarget);
    if (color) setBackgroundColor(color);
  }, []);

  const frameClassName = ['book-cover-frame', className].filter(Boolean).join(' ');
  const imageClasses = ['book-cover-frame__image', imageClassName].filter(Boolean).join(' ');
  const style = backgroundColor ? ({ '--book-cover-bg': backgroundColor } as React.CSSProperties) : undefined;

  return (
    <div className={frameClassName} style={style}>
      <img
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        className={imageClasses}
        onLoad={handleLoad}
      />
    </div>
  );
}

export default BookCoverFrame;
